import React, { useState, useEffect} from 'react';
import './SupervisorLogin.css';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { IoCloseOutline } from 'react-icons/io5';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { getSupabase } from '../../lib/supabaseClient';
import { useAuth } from '../../lib/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { SupervisorProfile } from '../../lib/database.types';

const SupervisorLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginState, setLoginState] = useState('');
  const navigate = useNavigate();
  const { session, user, userType } = useAuth() as { 
    session: any; 
    user: SupervisorProfile | null; 
    userType: 'student' | 'supervisor' | 'ojt_coordinator' | null; 
  };

  // Handle authentication state and navigation
  useEffect(() => {
    console.log('SupervisorLogin auth state check:', { 
      hasSession: !!session,
      hasUser: !!user,
      userType,
      timestamp: new Date().toISOString()
    });

    // Handle successful supervisor/coordinator login
    if (session && user && userType && (userType === 'supervisor' || userType === 'ojt_coordinator')) {
      console.log('Supervisor/Coordinator login confirmed, redirecting...');
      setError('');
      setLoading(false);
      
      if (userType === 'supervisor') {
        setLoginState('Redirecting to Supervisor Dashboard...');
        navigate('/supervisor/home', { replace: true });
      } else if (userType === 'ojt_coordinator') {
        setLoginState('Redirecting to OJT Coordinator Dashboard...');
        navigate('/ojt-coordinator/home', { replace: true });
      }
      return;
    }

    // Handle non-supervisor users - but only if they're actually students, not OJT coordinators
    if (session && user && userType && userType === 'student') {
      // Check if this is actually a student or if it's an OJT coordinator being misidentified
      const currentPath = window.location.pathname;
      const isOJTContext = currentPath.includes('/ojt-coordinator') || currentPath.includes('/trainees');
      
      if (isOJTContext) {
        console.log('OJT coordinator context detected, not signing out student user');
        return;
      }
      
      // Additional check: if user has supervisor profile, they might be an OJT coordinator
      if (user && 'user_role' in user && user.user_role === 'ojt_coordinator') {
        console.log('User has OJT coordinator role in profile, not signing out');
        return;
      }
      
      console.log('Student user detected on supervisor login, signing out...');
      setError('This login is only for Supervisors and OJT Coordinators. Please use the student login page.');
      setLoading(false);
      setLoginState('Redirecting to student login...');
      
      // Add a small delay before redirecting to give user time to read the message
      setTimeout(() => {
        const supabase = getSupabase();
        supabase.auth.signOut().then(() => {
          window.location.href = '/';
        });
      }, 2000);
      return;
    }

  }, [session, user, userType, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setLoginState('Logging in...');
    
    const supabase = getSupabase();
    
    try {
      console.log('Starting login process...');
      
      // Simple sign out if there's an existing session, but don't clear all storage
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        console.log('Found existing session, signing out...');
        await supabase.auth.signOut();
        // Wait a moment for sign out to complete
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      console.log('Attempting to sign in with email:', email);
      
      // Sign in with new credentials
      const signInResult = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInResult.error) {
        throw signInResult.error;
      }

      if (!signInResult.data?.user) {
        throw new Error('No user data returned from login');
      }

      console.log('Sign in successful:', {
        userId: signInResult.data.user.id,
        hasSession: !!signInResult.data.session,
        event: 'SIGNED_IN',
        timestamp: new Date().toISOString()
      });

      // Let the AuthProvider handle the rest - don't interfere with the normal flow
      console.log('Login complete, AuthProvider will handle profile loading and navigation');
      setLoginState('Login successful! Loading profile...');
      
    } catch (err) {
      console.error('Login error:', err);
      if (err instanceof Error) {
        // Make error messages more user-friendly
        if (err.message.includes('Invalid login')) {
          setError('Invalid email or password');
        } else if (err.message.includes('password')) {
          setError('Please check your password');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred');
      }
      setLoginState('');
      setLoading(false);
    }
  };

  const goToStudentLogin = () => {
    window.location.href = '/';
  };

  const goToForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="logo">
          <h1>InternTr<FaMapMarkerAlt className="location-icon" />ckr</h1>
          <p className="tagline">Anytime. Anywhere.</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label>Email</label>
            <div className="input-with-clear">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Please enter your email"
                required
              />
              {email && (
                <button
                  type="button"
                  className="clear-button"
                  onClick={() => setEmail('')}
                >
                  <IoCloseOutline size={18} />
                </button>
              )}
            </div>
          </div>
          <div className="input-group">
            <label>Password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Please enter your password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}
          {loginState && <div className="login-status">{loginState}</div>}
          <div className="actions-container">
            <div className="button-row">
              <button type="submit" className="action-button login-button" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
              <button 
                type="button" 
                className="action-button register-button" 
                onClick={() => window.location.href = '/supervisor-registration'}
                disabled={loading}
              >
                Register
              </button>
            </div>
          </div>
          <div className="forgot-password">
            <p>Forgot password? <a href="#" onClick={goToForgotPassword} className="link-button">Click here</a>.</p>
          </div>
        </form>
        <div className="student-link">
          <p>Not a OJT Coordinator / Supervisor? <button onClick={goToStudentLogin} className="link-button">Click here to sign in as a Student</button>.</p>
        </div>
      </div>
    </div>
  );
};

export default SupervisorLogin;
